<script>
  import { _, callout } from "../stores/index";
  import { AuthorizersRepository } from "../repositories/index";
  import {
    Flex,
    FlexItem,
    Button,
    Form,
    Submit,
    TextField,
    Modal,
    Select,
    SelectItem,
  } from "@gl/design-system";
  import { createEventDispatcher, onMount } from "svelte";
  const dispatch = createEventDispatcher();

  export let active;
  export let selectedAuthorizer;

  let loading = {};
  let form = {};

  const onSubmit = async () => {
    loading.submit = true;
    try {
      const payload = {
        name: form.name,
        jwksUri: form.jwksUri,
        issuer: form.issuer,
        type: form.type,
        externalUserManagement: form.externalUserManagement === "true",
        emailDomain: form.emailDomain,
      };
      if (payload.externalUserManagement && form.type === "auth0") {
        payload.domain = form.domain;
        payload.clientId = form.clientId;
        payload.clientSecret = form.clientSecret;
      } else if (payload.externalUserManagement && form.type === "okta") {
        if (form.token) payload.token = form.token;
        payload.orgUrl = form.orgUrl;
        payload.groupId = form.groupId;
      }
      const result = selectedAuthorizer
        ? await AuthorizersRepository.editAuthorizer(
            selectedAuthorizer.id,
            payload,
          )
        : await AuthorizersRepository.addAuthorizer(payload);

      if (result === "") {
        callout.set({
          msg: $_("common.nochanges"),
          status: "warning",
          timeout: true,
        });
        dispatch("close");
      } else {
        dispatch("success");
        dispatch("close");
      }
    } finally {
      loading.submit = false;
    }
  };

  onMount(() => {
    if (selectedAuthorizer) {
      form.name = selectedAuthorizer.data.name;
      form.jwksUri = selectedAuthorizer.data.jwksUri;
      form.issuer = selectedAuthorizer.data.issuer;
      form.type = selectedAuthorizer.data.type;
      form.externalUserManagement = String(
        selectedAuthorizer.data.externalUserManagement,
      );
      form.emailDomain = selectedAuthorizer.data.emailDomain ?? "";
      form.domain = selectedAuthorizer.data.domain ?? "";
      form.orgUrl = selectedAuthorizer.data.orgUrl ?? "";
      form.groupId = selectedAuthorizer.data.groupId ?? "";
    } else {
      form.type = "okta";
      form.externalUserManagement = "false";
    }
  });
</script>

<Modal bind:active boxed on:close>
  <div class="gl-title mb-4">
    {selectedAuthorizer ? $_("authorizers.edit") : $_("authorizers.new")}
  </div>
  <Form validationMode="eager">
    <TextField
      bind:value={form.name}
      id="authorizer-name"
      label={$_("common.name")}
      required={$_("validations.required")}
      maxLength={50}
      config={{ characterCount: $_("character.count") }}
    />

    <TextField
      bind:value={form.issuer}
      id="authorizer-issuer"
      label={$_("authorizers.issuer")}
      required={$_("validations.required")}
    />

    <TextField
      bind:value={form.jwksUri}
      id="authorizer-jwks"
      label={$_("authorizers.jwksUri")}
      required={$_("validations.required")}
    />

    <Select
      label={$_("common.type")}
      bind:value={form.type}
      id="authorizer-type"
      disabled={selectedAuthorizer}
    >
      <SelectItem label="Auth0" value="auth0" />
      <SelectItem label="Okta" value="okta" />
    </Select>

    <TextField
      bind:value={form.emailDomain}
      id="authorizer-email-domain"
      label={$_("authorizers.emailDomain")}
      required={$_("validations.required")}
      validate={(value) => [!/\s/.test(value), $_("validations.noSpaces")]}
    />

    <Select
      label={$_("authorizers.externalUserManagement")}
      bind:value={form.externalUserManagement}
      id="authorizer-ext-mgmt"
    >
      <SelectItem label={$_("common.yes")} value="true" />
      <SelectItem label={$_("common.no")} value="false" />
    </Select>

    {#if form.externalUserManagement === "true" && form.type === "auth0"}
      <TextField
        bind:value={form.domain}
        id="authorizer-domain"
        label={$_("authorizers.domain")}
        required={$_("validations.required")}
      />
      <TextField
        bind:value={form.clientId}
        id="authorizer-client-id"
        label={$_("authorizers.clientId")}
        required={$_("validations.required")}
      />
      <TextField
        bind:value={form.clientSecret}
        id="authorizer-client-secret"
        label={$_("authorizers.clientSecret")}
        required={$_("validations.required")}
        variant="Password"
      />
    {:else if form.externalUserManagement === "true" && form.type === "okta"}
      <TextField
        bind:value={form.token}
        id="authorizer-token"
        label={$_("authorizers.token")}
        placeholder={selectedAuthorizer
          ? "••••••••••••••••••••••••••••••••"
          : ""}
        validate={(value) => [
          value.length < 255,
          $_("validation.length", { values: { maxCharacters: 255 } }),
        ]}
      />
      <TextField
        bind:value={form.orgUrl}
        id="authorizer-org-url"
        label={$_("authorizers.orgUrl")}
        required={$_("validations.required")}
      />
      <TextField
        bind:value={form.groupId}
        id="authorizer-group-id"
        label={$_("authorizers.groupId")}
        required={$_("validations.required")}
      />
    {/if}

    <Flex>
      <FlexItem>
        <Submit
          loading={loading.submit}
          on:submit={onSubmit}
          label={$_("buttons.save")}
        />
      </FlexItem>
      <FlexItem class="ml-4">
        <Button outlined on:click={() => dispatch("close")}>
          {$_("buttons.cancel")}
        </Button>
      </FlexItem>
    </Flex>
  </Form>
</Modal>
